import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Split, Play, RefreshCw } from "lucide-react";


export default function ABTest() {
  const [strategies, setStrategies] = useState([]);
  const [stratId, setStratId] = useState("");
  const [sl, setSl] = useState(30);
  const [tp, setTp] = useState(60);
  const [lot, setLot] = useState(0.1);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/strategies").then(({ data }) => {
      setStrategies(data || []);
      // Functional set so we never read stratId from the closure (boot-only effect)
      setStratId((prev) => prev || (data?.[0]?.strategy_id ?? ""));
    }).catch(()=>{});
    api.get("/ab-tests").then(({data}) => setHistory(data || [])).catch(()=>{});
  }, []);

  const run = async () => {
    if (!stratId) { toast.error("Pick a strategy first"); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/ab-test", {
        strategy_id: stratId,
        variant_overrides: {
          stop_loss_pips: Number(sl),
          take_profit_pips: Number(tp),
          lot_size: Number(lot),
        },
      });
      setResult(data);
      setHistory(h => [data, ...h]);
      toast.success(data.winner === "b" ? "Variant B won!" : data.winner === "a" ? "Variant A won" : "Tie");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A/B run failed");
    } finally { setRunning(false); }
  };

  return (
    <div>
      <Navbar/>
      <main className="max-w-6xl mx-auto px-6 py-10">
        <header className="mb-6 flex items-center gap-4">
          <Split size={28} className="text-[var(--accent)]"/>
          <div>
            <h1 className="font-display text-4xl text-white">Strategy A/B Tester</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              Runs the same market against your base strategy (A) and a variant (B) with your override values.
              Same seed = identical price path, so any diff is attributable to the config change.
            </p>
          </div>
        </header>

        <section className="card-sharp p-5 mb-6" data-testid="ab-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Base strategy">
              <select data-testid="ab-strategy" className="input-dark"
                value={stratId} onChange={(e)=>setStratId(e.target.value)}>
                {strategies.length === 0 && <option value="">No strategies yet</option>}
                {strategies.map(s => <option key={s.strategy_id} value={s.strategy_id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Variant B — Stop-loss (pips)">
              <input data-testid="ab-sl" type="number" className="input-dark"
                value={sl} onChange={(e)=>setSl(e.target.value)}/>
            </Field>
            <Field label="Variant B — Take-profit (pips)">
              <input data-testid="ab-tp" type="number" className="input-dark"
                value={tp} onChange={(e)=>setTp(e.target.value)}/>
            </Field>
            <Field label="Variant B — Lot size">
              <input data-testid="ab-lot" type="number" step="0.01" className="input-dark"
                value={lot} onChange={(e)=>setLot(e.target.value)}/>
            </Field>
          </div>
          <div className="mt-5">
            <button data-testid="ab-run" onClick={run} disabled={running || !stratId}
              className="btn-primary flex items-center gap-2">
              {running ? <RefreshCw size={14} className="animate-spin"/> : <Play size={14}/>}
              Run A/B test
            </button>
          </div>
        </section>

        {result && <ABResultCard r={result}/>}

        {history.length > 0 && (
          <section className="mt-8">
            <h2 className="font-display text-lg text-white mb-3">Recent A/B tests</h2>
            <ul className="space-y-2 text-xs font-mono" data-testid="ab-history">
              {history.slice(0, 10).map((h) => (
                <li key={h.ab_test_id} className="border border-white/5 rounded p-2 flex items-center gap-3">
                  <span className={`chip ${h.winner==="b" ? "!text-[var(--profit)]" : h.winner==="a" ? "!text-[var(--accent)]" : ""}`}>
                    winner: {h.winner.toUpperCase()}
                  </span>
                  <span className="text-[var(--text-secondary)]">
                    A: ${h.a?.net_usd} · B: ${h.b?.net_usd} · Δ ${h.diff?.net_usd}
                  </span>
                  <span className="ml-auto text-[var(--text-muted)]">{h.created_at?.slice(0,16)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}


function ABResultCard({ r }) {
  const winnerColor = r.winner === "b" ? "text-[var(--profit)]" : r.winner === "a" ? "text-[var(--accent)]" : "text-[var(--text-muted)]";
  return (
    <section className="card-sharp p-5" data-testid="ab-result">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="font-display text-lg text-white">Result</h2>
        <span className={`chip ${winnerColor}`}>WINNER: {r.winner.toUpperCase()}</span>
        <span className="ml-auto text-[10px] font-mono text-[var(--text-muted)]">seed {r.seed} · {r.n_candles} candles</span>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-xs font-mono">
          <thead className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
            <tr>
              <th></th>
              <th className="text-right">SL/TP</th>
              <th className="text-right">Trades</th>
              <th className="text-right">Win %</th>
              <th className="text-right">Net pips</th>
              <th className="text-right pr-2">Net USD</th>
            </tr>
          </thead>
          <tbody>
            {["a","b"].map((v) => (
              <tr key={v} className="border-t border-white/5" data-testid={`ab-row-${v}`}>
                <td className="py-2"><span className="chip">Variant {v.toUpperCase()}</span></td>
                <td className="text-right">{r[v].sl}/{r[v].tp}</td>
                <td className="text-right">{r[v].trades}</td>
                <td className="text-right">{r[v].win_rate}%</td>
                <td className="text-right">{r[v].net_pips}</td>
                <td className={`text-right pr-2 ${r[v].net_usd >= 0 ? "text-[var(--profit)]" : "text-[var(--loss)]"}`}>
                  ${r[v].net_usd.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs font-mono text-[var(--text-muted)]">
        Δ trades: {r.diff.trades} · Δ win-rate: {r.diff.win_rate}% · Δ net: ${r.diff.net_usd}
      </div>
    </section>
  );
}


function Field({ label, children }) {
  return <div><label className="label-tiny">{label}</label>{children}</div>;
}
